import { ApiError } from "../../lib/api.ts";
import { CALL_ENDED_TEXT, CALL_GONE_TEXT, CALL_REMOVED_TEXT } from "./call-words.ts";

/**
 * Куда уходит комната звонка с преподавателями (6.4 и 9.5
 * `docs/teacher-calls-design.md`). Чистые правила без React, чтобы проверять
 * их встроенным `node:test`.
 *
 * **Зачем это вообще.** У урока любой разрыв комнаты ведёт в расписание, и
 * так и надо: занятие остаётся там. Звонка в расписании нет, а после конца нет
 * и в «Уроках»: завершённый звонок лежит в корзине, и его карточка отвечает
 * 404 ОБОИМ (раздел 4.4 проекта). Поэтому экран конца строится не по коду
 * отказа, а по причине отключения LiveKit, по перезапросу карточки и по памяти
 * вкладки.
 *
 * ⚠️ **Модуль лежит в стартовом чанке**: его берёт страница урока, а она
 * стартовая. Поэтому в нём нет ни `livekit-client`, ни чего-либо из комнаты:
 * причину отключения комната переводит в слово сама (`leaveWhyOf` принимает
 * ИМЯ причины, а не значение перечисления пакета), а импорт значением утащил бы
 * медиапакет в бандл каждого.
 */

/** Роль смотрящего в звонке. Совпадает с `Lesson.call.role`. */
export type CallRole = "host" | "guest";

/**
 * Почему комната ушла.
 *
 * - `room_deleted` комнату SFU закрыли: конец звонка;
 * - `removed` участника выкинул сервер: «убрать из звонка»;
 * - `left` человек вышел сам, кнопкой;
 * - `other` всё остальное: обрыв, отказ перевхода без причины;
 * - `gone` своя проверка после переподключения: карточка звонка пропала
 *   (`use-call-guard.ts`). От LiveKit такой причины не приходит.
 */
export type LeaveWhy = "room_deleted" | "removed" | "left" | "other" | "gone";

/**
 * Причина отключения LiveKit словом.
 *
 * Принимает ИМЯ из перечисления пакета (`DisconnectReason[reason]`), а не число:
 * сравнение с числом потребовало бы импорта перечисления значением, а модуль
 * стартовый (шапка). Неизвестное и отсутствующее это `other`: перевход, который
 * не удался, приезжает вовсе без причины (`livekit-client` 2.21.0, событие
 * `Disconnected` движка), и именно его обязана разобрать проверка карточки.
 */
export function leaveWhyOf(reasonName: string | undefined): LeaveWhy {
  if (reasonName === "ROOM_DELETED") return "room_deleted";
  if (reasonName === "PARTICIPANT_REMOVED") return "removed";
  if (reasonName === "CLIENT_INITIATED") return "left";
  return "other";
}

/** Что делает страница. */
export type CallExit =
  /** Урок: прежний выход в расписание. */
  | { to: "leave" }
  /**
   * Раздел «Уроки». `replace` у ведущего после конца: возвращаться «назад» в
   * завершённый звонок незачем, а у вышедшего кнопкой «назад» это самый дешёвый
   * путь обратно в живой звонок.
   */
  | { to: "lessons"; replace: boolean }
  /** Спросить карточку и решить по ответу (`afterRecheck`). */
  | { to: "recheck" }
  /** Экран конца с этими словами и кнопкой «К урокам». */
  | { to: "screen"; text: string };

/**
 * Решение по причине отключения. `role === null` это урок, а не звонок.
 *
 * ⚠️ **Роль нужна, и ровно в одном месте:** у ведущего `room_deleted` это его
 * же «Завершить». Сервер закрывает комнату ДО ответа ручки конца
 * (`routes/lessons/room.ts`), поэтому событие приходит раньше, чем страница
 * дождалась ответа, и ведущий без роли увидел бы «Звонок завершён» от
 * собственного нажатия.
 *
 * ⚠️ **Прочая причина не ведёт на «Уроки» сразу.** Гость, бывший без сети,
 * пока звонок завершили, возвращается без причины, и в «Уроках» звонка уже нет:
 * перезапрос карточки отличает «конец» от «обрыва».
 */
export function callExitOf(role: CallRole | null, why: LeaveWhy): CallExit {
  if (role === null) return { to: "leave" };
  if (why === "gone") return { to: "screen", text: goneText(role) };
  if (why === "left") return { to: "lessons", replace: false };
  if (role === "host") {
    return why === "room_deleted" ? { to: "lessons", replace: true } : { to: "recheck" };
  }
  if (why === "room_deleted") return { to: "screen", text: CALL_ENDED_TEXT };
  if (why === "removed") return { to: "screen", text: CALL_REMOVED_TEXT };
  return { to: "recheck" };
}

/**
 * Ответ карточки звонка: есть, пропала или не дозвонились.
 *
 * `null` это ответ пришёл. Пропавшей считается только 404 с НАШИМ кодом: чужой
 * 404 по дороге (страница прокси) приезжает кодом `unknown_error`, и объявлять
 * по нему конец звонка значит соврать про живой звонок. Тот же довод стоит у
 * `classifyTicketFailure` в `board/ticket-retry.ts`.
 */
export type CardCheck = "found" | "gone" | "failed";

export function cardCheckOf(error: unknown): CardCheck {
  if (error === null) return "found";
  if (error instanceof ApiError && error.status === 404 && error.code !== "unknown_error") {
    return "gone";
  }
  return "failed";
}

/**
 * Решение после перезапроса карточки на прочую причину. Пропала: экран конца
 * по роли; есть или не дозвонились: «Уроки», где звонок есть или откуда видно,
 * что со связью.
 */
export function afterRecheck(role: CallRole, check: CardCheck): CallExit {
  if (check === "gone") return { to: "screen", text: goneText(role) };
  return { to: "lessons", replace: false };
}

/**
 * Уходить ли после переподключения LiveKit (`RoomEvent.Reconnected`).
 *
 * ⚠️ **Зачем.** «Убрать» выкидывает из видео сейчас, но токен не отзывает, а
 * клиент, потерявший сеть, при возврате перевходит сохранённым токеном сам
 * (5.3 проекта). Убранный без сети вернулся бы в звонок, и ведущий видел бы его
 * плитку снова. Проверка карточки после переподключения это наш клиент,
 * выходящий сам. Не дозвонились: остаёмся, дверь не захлопывается из-за
 * секундной ямки.
 */
export function afterReconnect(check: CardCheck): boolean {
  return check === "gone";
}

/**
 * Слова экрана, когда карточки звонка больше нет, а причину знать неоткуда.
 *
 * У гостя это конец ИЛИ удаление: ответ один на обе причины. У ведущего
 * удаления не бывает, звонок завершён.
 */
export function goneText(role: CallRole): string {
  return role === "guest" ? CALL_GONE_TEXT : CALL_ENDED_TEXT;
}

/**
 * Конец звонка из ручки «Завершить» считается сделанным и при 404: звонок уже
 * завершили из другой вкладки ведущего, и он в корзине (9.5 проекта).
 */
export function endedAlready(error: unknown): boolean {
  return cardCheckOf(error) === "gone";
}

/* ──────────────────────── исполнение решений ──────────────────────────── */

/** Что исполнитель умеет делать со страницей. Всё подменяемое, ради теста. */
export interface CallExitDeps {
  /** Моя роль в звонке, либо `null` у урока. */
  role: CallRole | null;
  /** Урок: прежний выход в расписание. */
  leaveLesson: () => void;
  toLessons: (replace: boolean) => void;
  toScreen: (text: string) => void;
  /** Перезапрос карточки звонка. Не бросает: отказ это `failed`. */
  checkCard: () => Promise<CardCheck>;
}

export interface CallExitRunner {
  /** Комната ушла (`onLeave`): причина словом, без причины это выход кнопкой. */
  leave: (why?: LeaveWhy) => void;
  /** «Завершить» прошло (или 404, 9.5 проекта): ведущий уходит на «Уроки». */
  ended: () => void;
  /** Экран конца сразу: перезагрузка вкладки после конца или удаления. */
  show: (text: string) => void;
  /**
   * Страница ушла (размонтирование, смена урока в адресе): дальше исполнитель
   * молчит. Поздний ответ перезапроса после «Назад» в браузере иначе увёл бы
   * человека с новой страницы на «Уроки».
   */
  dispose: () => void;
  /**
   * Снова в деле после `dispose`. Нужен ровно `StrictMode`: в разработке он
   * снимает эффект и ставит заново, и без этого исполнитель молчал бы с
   * первого же монтирования. Решение «уже решено» он не отменяет.
   */
  resume: () => void;
}

/**
 * Исполнение решений `callExitOf` с памятью о том, что уход уже решён.
 *
 * ⚠️ **Уход приходит несколькими событиями подряд, и решение одно.** Ведущий
 * жмёт «Завершить»: сервер закрывает комнату до ответа, и `ROOM_DELETED`
 * приходит раньше ответа ручки, а следом размонтирование рвёт соединение ещё
 * раз. Гостя убирают: `PARTICIPANT_REMOVED`, затем экран конца размонтирует
 * комнату. Без памяти второе событие увело бы с экрана конца на «Уроки».
 *
 * ⚠️ **Пока идёт перезапрос карточки, прочие разрывы молчат**, а «Завершить»
 * нет: ответ ручки конца это факт, и он перебивает ещё не пришедший ответ
 * карточки. Пришедший после него ответ карточки не исполняется.
 *
 * У урока памяти нет: каждый `leave` это прежний выход, как было до звонков.
 * Зависимости приезжают функцией, чтобы исполнитель читал свежие.
 *
 * ⚠️ После `dispose` молчит всё, включая урок: страницы, которой он служил,
 * уже нет.
 */
export function callExitRunner(deps: () => CallExitDeps): CallExitRunner {
  let decided = false;
  let pending = false;
  let off = false;
  const apply = (exit: CallExit): void => {
    if (off) return;
    const d = deps();
    if (exit.to === "leave") {
      d.leaveLesson();
      return;
    }
    if (decided) return;
    if (exit.to === "screen") {
      decided = true;
      d.toScreen(exit.text);
      return;
    }
    if (exit.to === "lessons") {
      decided = true;
      d.toLessons(exit.replace);
      return;
    }
    if (pending) return;
    pending = true;
    void d
      .checkCard()
      .catch((): CardCheck => "failed")
      .then((check) => {
        pending = false;
        const role = deps().role;
        if (!decided && !off && role !== null) apply(afterRecheck(role, check));
      });
  };
  return {
    leave: (why) => apply(callExitOf(deps().role, why ?? "left")),
    ended: () => {
      if (decided || off) return;
      decided = true;
      deps().toLessons(true);
    },
    show: (text) => {
      if (decided || off) return;
      decided = true;
      deps().toScreen(text);
    },
    dispose: () => {
      off = true;
    },
    resume: () => {
      off = false;
    },
  };
}

/* ─────────────────────────── память вкладки ──────────────────────────── */

/**
 * Отметка «эта вкладка была в звонке» для понятного экрана после перезагрузки.
 *
 * ⚠️ **Без неё перезагрузка после конца говорила бы «Урок не найден или у вас
 * нет к нему доступа»**: карточка завершённого звонка отвечает 404, и сервер не
 * различает «не было» и «было и кончилось» намеренно. Вкладка помнит, что
 * различие есть. Хранилище сессионное: оно живёт ровно вкладку, а «вы были в
 * этом звонке» в другой вкладке или завтра не значит ничего.
 */
export function callSeenKey(lessonId: string): string {
  return `edu.call-seen.${lessonId}`;
}

/** Сессионное хранилище вкладки или `null`, если его нет или оно запрещено. */
export function tabStore(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

/** Запомнить роль во вкладке. Отказ хранилища молчит: экран станет общим, и только. */
export function markCallSeen(store: Storage | null, lessonId: string, role: CallRole): void {
  try {
    store?.setItem(callSeenKey(lessonId), role);
  } catch {
    // Приватное окно и запрещённое хранилище: без отметки, а не без звонка
  }
}

/** Роль, с которой вкладка была в этом звонке, либо `null`. */
export function callSeenOf(store: Storage | null, lessonId: string): CallRole | null {
  try {
    const raw = store?.getItem(callSeenKey(lessonId)) ?? null;
    return raw === "host" || raw === "guest" ? raw : null;
  } catch {
    return null;
  }
}
